import { MealPlanKind, type RoomInfoProducts } from "@b2bportal/lodging-api";

export const getAllUniqueMealPlansInRoom = (
  room: RoomInfoProducts
): MealPlanKind[] => {
  const mealPlans = room.products.map(
    (p) => p.mealPlan?.kind ?? MealPlanKind.RoomOnly
  );
  return Array.from(new Set(mealPlans));
};

/**
 * Returns true if the same meal plan is available in ALL room products.
 * Used to determine if the meal plan should be displayed at the top of the
 * room card instead of per-rate.
 */
export const isOnlyOneMealPlanAvailableOnAllProducts = (
  room: RoomInfoProducts
): boolean => {
  const uniqueMealPlans = getAllUniqueMealPlansInRoom(room);
  return (
    uniqueMealPlans.length === 1 && uniqueMealPlans[0] !== MealPlanKind.RoomOnly
  );
};

/**
 * Returns the single meal plan kind shared by all products in a room,
 * or undefined if products have different meal plans or only "RoomOnly".
 */
export const getRoomLevelMealPlanKind = (
  room: RoomInfoProducts
): MealPlanKind | undefined => {
  if (!isOnlyOneMealPlanAvailableOnAllProducts(room)) {
    return undefined;
  }
  return getAllUniqueMealPlansInRoom(room)[0];
};

/**
 * Returns the single meal plan kind shared across ALL rooms in a hotel,
 * or undefined if rooms have different meal plans or no common plan.
 * Each room must have a single consistent meal plan (non-RoomOnly) across
 * all its products, and that plan must be the same for every room.
 */
export const getHotelLevelMealPlanKind = (
  rooms: RoomInfoProducts[]
): MealPlanKind | undefined => {
  if (rooms.length === 0) return undefined;

  // Collect all meal plan kinds from every rate across all rooms
  const allMealPlans = rooms.flatMap((room) =>
    room.products.map((p) => p.mealPlan?.kind ?? MealPlanKind.RoomOnly)
  );

  if (allMealPlans.length === 0) return undefined;

  const uniquePlans = new Set(allMealPlans);

  // All rates must share the same non-RoomOnly meal plan
  if (uniquePlans.size !== 1) return undefined;

  const plan = allMealPlans[0];
  return plan !== MealPlanKind.RoomOnly ? plan : undefined;
};
